import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { refusalProse } from '@backrs_ui/grow-ui/src/components/InstitutionWrite/refusalProse';
import {
  landed,
  refused,
  type WriteOutcome,
} from '@backrs_ui/grow-ui/src/components/InstitutionWrite/writeOutcome';
import { useOperatorInstitutionSwitch } from '@backrs_ui/grow-ui/src/hooks/useOperatorInstitutionSwitch';
import {
  applyParamUpdates,
  DASHBOARD_PARAM,
  parseNullableIntParam,
} from '@backrs_ui/grow-ui/src/pages/CoachingDashboard/dashboardUrlParams';
import { cohortLabel } from '@backrs_ui/grow-ui/src/pages/CoachingDashboard/namedRefLabel';
import type { CoachSummary } from '@backrs_ui/grow-ui/src/redux/coachingDashboard/coachingDashboard.types';
import {
  firstOperatorContextRefusal,
  isLandedUnreadable,
  useAttachCohortCoachMutation,
  useCreateCohortMutation,
  useDetachCohortCoachMutation,
  useGetAdminRostersQuery,
  useRenameCohortMutation,
  type AdminCohortCoach,
  type AdminRoster,
  type CohortDraft,
} from '@backrs_ui/grow-ui/src/redux/institutionAdmin';
import { personName } from '@backrs_ui/grow-ui/src/utils/personName';

import { skipToken } from '@reduxjs/toolkit/query';

import { cohortCoachLabel } from './cohortCoaches';
import {
  coachesDialogStrings,
  cohortStrings,
  createdMessage,
  renameStrings,
} from './institutionAdminWriteMessages';
import { landedProse, writeProse } from './institutionAdminWriteProse';
import {
  defaultRostersAdminTab,
  matchesInstitution,
  parseRostersAdminTab,
  ROSTERS_ADMIN_PARAM,
  rosterInstitutions,
  type RostersAdminTab,
} from './rostersAdminTabs';
import { useCodeDoors } from './useCodeDoors';

export type InstitutionAdminControllerArgs = Readonly<{
  enabled: boolean;
  tabs: readonly RostersAdminTab[];
}>;

export const useInstitutionAdminController = ({
  enabled,
  tabs,
}: InstitutionAdminControllerArgs) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const onOperatorInstitutionSelect = useOperatorInstitutionSwitch();

  const tab = parseRostersAdminTab(searchParams.get(ROSTERS_ADMIN_PARAM.tab), tabs);
  const selectedInstitutionId = parseNullableIntParam(
    searchParams.get(DASHBOARD_PARAM.institutionId),
  );
  const cohortRosterId = parseNullableIntParam(searchParams.get(DASHBOARD_PARAM.rosterId));

  const updateParams = useCallback(
    (updates: Record<string, string | number | null>, options?: { replace?: boolean }) => {
      setSearchParams((prev) => applyParamUpdates(prev, updates), options);
    },
    [setSearchParams],
  );

  const rosters = useGetAdminRostersQuery(enabled ? {} : skipToken);

  const codeDoors = useCodeDoors();
  const [createCohort, createCohortState] = useCreateCohortMutation();
  const [attachCoach, attachCoachState] = useAttachCohortCoachMutation();
  const [detachCoach, detachCoachState] = useDetachCohortCoachMutation();
  const [renameCohort, renameCohortState] = useRenameCohortMutation();

  const heldRoots = enabled
    ? firstOperatorContextRefusal([
        rosters.error,
        createCohortState.error,
        ...codeDoors.errors,
        attachCoachState.error,
        detachCoachState.error,
        renameCohortState.error,
      ])
    : null;

  const allRosterRows = useMemo(() => rosters.data?.data ?? [], [rosters.data]);
  const institutions = useMemo(() => rosterInstitutions(allRosterRows), [allRosterRows]);

  // Both filters narrow what was read rather than what is asked for. The read takes no
  // kind parameter, and narrowing the read by institution would collapse the very set the
  // institution options are drawn from.
  const rosterRows = useMemo<AdminRoster[]>(
    () => allRosterRows.filter((roster) => matchesInstitution(roster, selectedInstitutionId)),
    [allRosterRows, selectedInstitutionId],
  );

  const onTabChange = useCallback(
    (next: RostersAdminTab) => {
      updateParams({
        [ROSTERS_ADMIN_PARAM.tab]: next === defaultRostersAdminTab(tabs) ? null : next,
      });
    },
    [tabs, updateParams],
  );

  const onInstitutionChange = useCallback(
    (institutionId: number | null) =>
      updateParams({ [DASHBOARD_PARAM.institutionId]: institutionId }),
    [updateParams],
  );

  const onCohortRosterChange = useCallback(
    (rosterId: number | null) => updateParams({ [DASHBOARD_PARAM.rosterId]: rosterId }),
    [updateParams],
  );

  const [detachCoachId, setDetachCoachId] = useState<number | null>(null);

  // A narrowed-away roster is no longer a line anyone is looking at, so the cohorts tab's
  // filter must not keep pointing at one the list no longer offers.
  useEffect(() => {
    if (cohortRosterId === null || !rosters.isSuccess || rosters.isFetching) return;
    if (rosterRows.some((roster) => roster.id === cohortRosterId)) return;

    updateParams({ [DASHBOARD_PARAM.rosterId]: null }, { replace: true });
  }, [cohortRosterId, rosterRows, rosters.isFetching, rosters.isSuccess, updateParams]);

  const onCreateCohort = useCallback(
    async (rosterId: number, draft: CohortDraft): Promise<WriteOutcome> => {
      const outcome = await createCohort({ rosterId, ...draft })
        .unwrap()
        .then((cohort) => ({ cohort, error: null as unknown }))
        .catch((error: unknown) => {
          console.error('Cohort create failed', error);

          return { cohort: null, error };
        });

      if (outcome.cohort !== null) return landed(createdMessage(outcome.cohort));

      if (isLandedUnreadable(outcome.error)) {
        // No `landedProse` here, unlike the two code doors: this arm is only reachable through
        // the client-minted reason, so there is no server prose to prefer. If the api ever adds a
        // cohort-side landed-unreadable reason to the set, this has to grow one.
        return landed(cohortStrings.create.unreadable);
      }

      return refused(writeProse(outcome.error));
    },
    [createCohort],
  );

  /**
   * The attachment row the door answers with is not read back, so the confirmation is minted
   * from what was asked for. The cohort is named by the caller rather than looked up here:
   * the name a rename has just changed lives in the read, not in this hook.
   */
  const onAttachCoach = useCallback(
    async (
      rosterId: number,
      cohortId: number,
      cohortName: string,
      coach: CoachSummary,
    ): Promise<WriteOutcome> => {
      const error = await attachCoach({ rosterId, cohortId, coachUserId: coach.id })
        .unwrap()
        .then(() => null as unknown)
        .catch((caught: unknown) => {
          console.error('Cohort coach attach failed', caught);

          return caught;
        });

      return error === null
        ? landed(coachesDialogStrings.attached(personName(coach), cohortName))
        : refused(writeProse(error));
    },
    [attachCoach],
  );

  const onDetachCoach = useCallback(
    async (
      rosterId: number,
      cohortId: number,
      cohortName: string,
      coach: AdminCohortCoach,
    ): Promise<WriteOutcome> => {
      setDetachCoachId(coach.id);

      const error = await detachCoach({ rosterId, cohortId, coachUserId: coach.id })
        .unwrap()
        .then(() => null as unknown)
        .catch((caught: unknown) => {
          console.error('Cohort coach detach failed', caught);

          return caught;
        });

      return error === null
        ? landed(coachesDialogStrings.detached(cohortCoachLabel(coach), cohortName))
        : refused(writeProse(error));
    },
    [detachCoach],
  );

  const onRenameCohort = useCallback(
    async (rosterId: number, cohortId: number, name: string): Promise<WriteOutcome> => {
      const outcome = await renameCohort({ rosterId, cohortId, name })
        .unwrap()
        .then((renamed) => ({ renamed, error: null as unknown }))
        .catch((error: unknown) => {
          console.error('Cohort rename failed', error);

          return { renamed: null, error };
        });

      if (outcome.renamed !== null) {
        return landed(renameStrings.renamed(cohortLabel(outcome.renamed)));
      }

      if (isLandedUnreadable(outcome.error)) {
        return landed(landedProse(outcome.error, renameStrings.unreadable));
      }

      return refused(writeProse(outcome.error));
    },
    [renameCohort],
  );

  // The label is line-scoped so no other line reads as busy; the GUARD is not, because the
  // request in flight is not scoped to anything.
  const isWriteInFlight =
    createCohortState.isLoading ||
    codeDoors.isWriteInFlight ||
    attachCoachState.isLoading ||
    detachCoachState.isLoading ||
    renameCohortState.isLoading;

  return {
    tab,
    onTabChange,
    onOperatorInstitutionSelect,
    heldRoots,
    selectedInstitutionId,
    onInstitutionChange,
    institutions,
    cohortRosterId,
    onCohortRosterChange,
    isRostersUnavailable: rosters.isError,
    isWriteInFlight,
    rosters: {
      rows: rosterRows,
      dropped: rosters.data?.dropped ?? 0,
      isLoading: rosters.isFetching,
      isError: rosters.isError,
      errorMessage: refusalProse(rosters.error),
    },
    codeCreate: codeDoors.codeCreate,
    codeRevoke: codeDoors.codeRevoke,
    cohortCoaches: {
      isAttaching: attachCoachState.isLoading,
      isDetachingFor: (coachUserId: number) =>
        detachCoachState.isLoading && detachCoachId === coachUserId,
      onAttach: onAttachCoach,
      onDetach: onDetachCoach,
    },
    cohortRename: {
      isSubmitting: renameCohortState.isLoading,
      isError: renameCohortState.isError && !isLandedUnreadable(renameCohortState.error),
      errorMessage: writeProse(renameCohortState.error),
      /** A refusal belongs to the opening that produced it; the next one starts clean. */
      reset: renameCohortState.reset,
      onSubmit: onRenameCohort,
    },
    cohortCreate: {
      isPending: createCohortState.isLoading,
      isError: createCohortState.isError && !isLandedUnreadable(createCohortState.error),
      errorMessage: writeProse(createCohortState.error),
      /** A refusal belongs to the opening that produced it; the next one starts clean. */
      reset: createCohortState.reset,
      onSubmit: onCreateCohort,
    },
  };
};
